import React, { useState, useEffect } from 'react';
import { 
  ChartBarIcon, 
  UsersIcon, 
  BuildingOfficeIcon,
  ClipboardDocumentListIcon, 
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import apiClient from '../../utils/api';

interface DailyStat {
  date: string;
  count: number;
}

interface TopLocationStat {
  location_id: string;
  location_name: string;
  entries: number;
}

interface ModuleUsageStat {
  module: string;
  count: number;
}

interface SuperAdminSystemStats {
  period_start: string;
  period_end: string;
  overview: {
    total_accounts: number;
    free_accounts: number;
    pro_accounts: number;
    total_users: number;
    active_users: number;
    total_locations: number;
  };
  activity: {
    total_entries: number;
    entries_today: number;
    entries_last_7d: number;
  };
  growth: {
    new_signups_last_7d: number;
    new_signups_last_30d: number;
  };
  daily_signups: DailyStat[];
  daily_entries: DailyStat[];
  top_locations: TopLocationStat[];
  module_usage: ModuleUsageStat[];
}

const SystemReports: React.FC = () => {
  const [stats, setStats] = useState<SuperAdminSystemStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadSystemStats();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSystemStats = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiClient.get<SuperAdminSystemStats>('/admin/system-reports');
      setStats(response.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load system statistics');
    } finally {
      setLoading(false);
    }
  };

  const StatCard: React.FC<{
    title: string;
    value: number | string;
    icon: React.ComponentType<any>;
    color: string;
    subtitle?: string;
  }> = ({ title, value, icon: Icon, color, subtitle }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5">
      <div className="flex items-center">
        <div className={`flex-shrink-0 p-3 rounded-lg ${color}`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div className="ml-3 flex-1 min-w-0">
          <p className="text-xs sm:text-sm font-medium text-gray-500">{title}</p>
          <p className="text-xl sm:text-2xl font-semibold text-gray-900">{value}</p>
          {subtitle && (
            <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
          )}
        </div>
      </div>
    </div>
  );

  const maxDailyEntries = Math.max(1, ...(stats?.daily_entries.map((d) => d.count) || [1]));
  const maxDailySignups = Math.max(1, ...(stats?.daily_signups.map((d) => d.count) || [1]));
  const proConversionPct = stats
    ? Math.round((stats.overview.pro_accounts / Math.max(1, stats.overview.total_accounts)) * 100)
    : 0;
  const activeUserPct = stats
    ? Math.round((stats.overview.active_users / Math.max(1, stats.overview.total_users)) * 100)
    : 0;
  const entriesPerAccount = stats
    ? (stats.activity.total_entries / Math.max(1, stats.overview.total_accounts)).toFixed(1)
    : '0.0';
  const farmsPerAccount = stats
    ? (stats.overview.total_locations / Math.max(1, stats.overview.total_accounts)).toFixed(2)
    : '0.00';

  const recent7Entries = stats?.daily_entries.slice(-7).reduce((sum, item) => sum + item.count, 0) || 0;
  const previous7Entries = stats?.daily_entries.slice(-14, -7).reduce((sum, item) => sum + item.count, 0) || 0;
  const recent7Signups = stats?.daily_signups.slice(-7).reduce((sum, item) => sum + item.count, 0) || 0;
  const previous7Signups = stats?.daily_signups.slice(-14, -7).reduce((sum, item) => sum + item.count, 0) || 0;
  const entriesMomentumPct = previous7Entries > 0
    ? Math.round(((recent7Entries - previous7Entries) / previous7Entries) * 100)
    : (recent7Entries > 0 ? 100 : 0);
  const signupMomentumPct = previous7Signups > 0
    ? Math.round(((recent7Signups - previous7Signups) / previous7Signups) * 100)
    : (recent7Signups > 0 ? 100 : 0);

  if (loading) {
    return (
      <div className="container-mobile py-8">
        <div className="flex justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">SaaS Performance Dashboard</h1>
        <p className="text-gray-600 mt-1">
          Superadmin analytics for adoption, conversion, engagement, and usage quality
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {stats && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Total Accounts"
              value={stats.overview.total_accounts}
              icon={UsersIcon}
              color="bg-blue-600"
              subtitle={`${stats.overview.pro_accounts} PRO / ${stats.overview.free_accounts} Free`}
            />
            <StatCard
              title="Active Users"
              value={stats.overview.active_users}
              icon={UsersIcon}
              color="bg-emerald-600"
              subtitle={`${stats.overview.total_users} total users`}
            />
            <StatCard
              title="Total Farms"
              value={stats.overview.total_locations}
              icon={BuildingOfficeIcon}
              color="bg-indigo-600"
            />
            <StatCard
              title="Total Data Entries"
              value={stats.activity.total_entries}
              icon={ClipboardDocumentListIcon}
              color="bg-purple-600"
              subtitle={`${stats.activity.entries_last_7d} in last 7 days`}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Entries Today"
              value={stats.activity.entries_today}
              icon={ChartBarIcon}
              color="bg-orange-600"
            />
            <StatCard
              title="Pro Conversion"
              value={`${proConversionPct}%`}
              icon={ArrowTrendingUpIcon}
              color="bg-violet-600"
              subtitle={`${stats.overview.pro_accounts} of ${stats.overview.total_accounts} accounts`}
            />
            <StatCard
              title="User Activity Rate"
              value={`${activeUserPct}%`}
              icon={UsersIcon}
              color="bg-emerald-600"
              subtitle={`${stats.overview.active_users} active users`}
            />
            <StatCard
              title="Entries / Account"
              value={entriesPerAccount}
              icon={ClipboardDocumentListIcon}
              color="bg-indigo-600"
              subtitle={`${farmsPerAccount} farms per account`}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <StatCard
              title="New Signups (7 Days)"
              value={stats.growth.new_signups_last_7d}
              icon={ArrowTrendingUpIcon}
              color="bg-cyan-600"
              subtitle={`${signupMomentumPct >= 0 ? '+' : ''}${signupMomentumPct}% vs previous 7 days`}
            />
            <StatCard
              title="New Signups (30 Days)"
              value={stats.growth.new_signups_last_30d}
              icon={ArrowTrendingUpIcon}
              color="bg-teal-600"
            />
            <StatCard
              title="Entries (Last 7 Days)"
              value={recent7Entries}
              icon={ChartBarIcon}
              color="bg-blue-600"
              subtitle={`${entriesMomentumPct >= 0 ? '+' : ''}${entriesMomentumPct}% vs previous 7 days`}
            />
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Daily Product Activity</h2>
            <div className="space-y-4">
              {stats.daily_entries.slice(-10).map((point) => (
                <div key={`entry-${point.date}`}>
                  <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600 mb-1">
                    <span>{point.date}</span>
                    <span>{point.count} entries</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${Math.max((point.count / maxDailyEntries) * 100, point.count > 0 ? 4 : 0)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Daily Signup Trend</h2>
            <div className="space-y-4">
              {stats.daily_signups.slice(-10).map((point) => (
                <div key={`signup-${point.date}`}>
                  <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600 mb-1">
                    <span>{point.date}</span>
                    <span>{point.count} signups</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full"
                      style={{ width: `${Math.max((point.count / maxDailySignups) * 100, point.count > 0 ? 4 : 0)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Active Locations</h2>
            {stats.top_locations.length === 0 ? (
              <p className="text-sm text-gray-500">No activity in selected period.</p>
            ) : (
              <div className="space-y-3">
                {stats.top_locations.map((loc, index) => (
                  <div key={loc.location_id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 break-words">{index + 1}. {loc.location_name}</p>
                    </div>
                    <p className="text-sm text-gray-600">{loc.entries} entries</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Module Usage Mix</h2>
            {stats.module_usage.length === 0 ? (
              <p className="text-sm text-gray-500">No usage data in selected period.</p>
            ) : (
              <div className="space-y-3">
                {stats.module_usage.map((mod) => (
                  <div key={mod.module}>
                    <div className="flex items-center justify-between text-sm text-gray-700 mb-1">
                      <span>{mod.module}</span>
                      <span>{mod.count}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-purple-600 h-2 rounded-full"
                        style={{ width: `${Math.max((mod.count / Math.max(1, stats.activity.total_entries)) * 100, mod.count > 0 ? 2 : 0)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Owner Insights</h2>
            <div className="space-y-2 text-sm text-gray-700">
              <p>
                Accounts are <span className="font-medium">{proConversionPct}% PRO</span> with a
                weekly signup trend of <span className="font-medium">{signupMomentumPct >= 0 ? '+' : ''}{signupMomentumPct}%</span>.
              </p>
              <p>
                Product activity is <span className="font-medium">{recent7Entries}</span> entries in the last 7 days
                ({entriesMomentumPct >= 0 ? '+' : ''}{entriesMomentumPct}% vs previous week).
              </p>
              <p>
                Average usage intensity is <span className="font-medium">{entriesPerAccount}</span> entries per account
                in the current reporting window.
              </p>
              <p>
                Top location concentration: <span className="font-medium">
                  {stats.top_locations[0] ? `${stats.top_locations[0].location_name} (${stats.top_locations[0].entries} entries)` : 'No dominant location yet'}
                </span>.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SystemReports;